/**
 * src/components/SignaturePad.jsx — canvas signature capture.
 *
 * Built on Pointer Events rather than separate mouse and touch handlers: one
 * code path covers mouse, finger and stylus, which is the whole point when the
 * client could be signing on anything.
 *
 * Two details that are easy to miss and make the difference between a
 * signature that looks like a signature and one that looks like a fax:
 *
 *  - The canvas is sized to the device pixel ratio and scaled back down in CSS,
 *    so strokes are crisp on a phone rather than blocky.
 *  - The canvas is filled white before drawing. A transparent PNG embedded in
 *    the PDF would render as an invisible signature on some viewers.
 */
import { useCallback, useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';

/**
 * @typedef {object} SignaturePadHandle
 * @property {() => string|null} toDataURL Returns the PNG data URL, or null if
 *   nothing has been drawn.
 * @property {() => void} clear Wipes the canvas.
 */

/**
 * Signature capture canvas.
 *
 * @param {{ disabled?: boolean, height?: number, onChange?: (hasInk: boolean) => void }} props
 * @param {React.Ref<SignaturePadHandle>} ref
 * @returns {JSX.Element}
 */
const SignaturePad = forwardRef(function SignaturePad(
  { disabled = false, height = 180, onChange },
  ref,
) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  /** Prepares the backing store at device resolution and paints it white. */
  const prepare = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const width = canvas.clientWidth || 400;

    canvas.width = width * ratio;
    canvas.height = height * ratio;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext('2d');
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    // Opaque background — see the file header.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111111';
  }, [height]);

  useEffect(() => {
    prepare();
    // Re-prepare on resize: the canvas is fluid-width, and a rotated phone
    // would otherwise stretch the existing strokes.
    window.addEventListener('resize', prepare);
    return () => window.removeEventListener('resize', prepare);
  }, [prepare]);

  /**
   * Converts a pointer event to canvas coordinates.
   *
   * @param {React.PointerEvent<HTMLCanvasElement>} e
   * @returns {{ x: number, y: number }}
   */
  const point = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e) => {
    if (disabled) return;
    drawing.current = true;
    // Capture the pointer so a stroke that leaves the canvas still ends
    // cleanly instead of leaving the pad stuck in drawing mode.
    e.currentTarget.setPointerCapture(e.pointerId);
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = point(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const move = (e) => {
    if (!drawing.current || disabled) return;
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = point(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!hasInk) {
      setHasInk(true);
      onChange?.(true);
    }
  };

  const end = () => {
    drawing.current = false;
  };

  useImperativeHandle(
    ref,
    () => ({
      toDataURL: () => (hasInk ? canvasRef.current.toDataURL('image/png') : null),
      clear: () => {
        prepare();
        setHasInk(false);
        onChange?.(false);
      },
    }),
    [hasInk, prepare, onChange],
  );

  return (
    <div className="stack">
      <canvas
        ref={canvasRef}
        className={`signature-pad${disabled ? ' disabled' : ''}${hasInk ? ' has-ink' : ''}`}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
        onPointerLeave={end}
        aria-label="Signature pad"
      />
      <div className="row-between">
        <span className="muted small">
          {disabled
            ? 'Read the form to the bottom to sign.'
            : hasInk
              ? 'Looks good. Clear it if you want another go.'
              : 'Sign above with your finger, stylus or mouse.'}
        </span>
        <button
          type="button"
          className="btn btn-sm"
          disabled={disabled || !hasInk}
          onClick={() => {
            prepare();
            setHasInk(false);
            onChange?.(false);
          }}
        >
          Clear
        </button>
      </div>
    </div>
  );
});

export default SignaturePad;
